
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { ArrowLeft } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const ProxyTOS = () => {
  const location = useLocation();
  const fromGeneralTOS = location.state?.from === 'general-tos';
  
  const backLink = fromGeneralTOS ? '/general-tos' : '/residential-proxies';
  const backText = fromGeneralTOS ? 'Back to General TOS' : 'Back to Residential Proxies';

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <div className="mb-8">
            <Link to={backLink}>
              <Button variant="ghost" className="text-nexx-green hover:text-green-700">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {backText}
              </Button>
            </Link>
          </div>

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              NEXX.NET – Proxy Terms of Service
            </h1>
            <p className="text-lg text-gray-600">
              Effective Date: June 4th, 2025
            </p>
            <div className="w-24 h-px bg-gray-300 mx-auto mt-6"></div>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            {/* Philosophy Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <span className="text-2xl mr-3">🌐</span>
                Our Philosophy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Residential proxies—when used responsibly—add tremendous value to the digital ecosystem. 
                They support fair access, enable secure research, fuel innovation in automation, and allow 
                users to interact with the internet at scale—without compromising privacy.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                At NEXX, we're committed to providing clean, stable, and contract-backed proxy access—and 
                that means we have to protect our infrastructure, our customers, and our provider relationships 
                with clear boundaries.
              </p>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Prohibited Activities Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <span className="text-2xl mr-3">🚫</span>
                Prohibited Activities (Even If Not Illegal)
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Some actions may not be explicitly illegal in all jurisdictions, but they still create 
                unacceptable risk to our network. These actions are strictly forbidden on the NEXX.NET 
                proxy platform:
              </p>
              
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Mass Emailing or Mailing Services</h3>
                  <p className="text-gray-700">Using proxies to send bulk emails or access platforms like SMTP, Mailchimp, SendGrid, etc.</p>
                </div>
                
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Accessing Pornographic or Adult Content</h3>
                  <p className="text-gray-700">Connecting to adult content or pornographic websites of any kind.</p>
                </div>
                
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Interacting with Financial Institutions</h3>
                  <p className="text-gray-700">Logging into, scraping, or automating access to online banking, fintech apps, or investment platforms.</p>
                </div>
                
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Bypassing Security Measures</h3>
                  <p className="text-gray-700">Using proxies for brute-force attacks, account cracking, CAPTCHA solving, or evading platform protections (e.g., brute-forcing Sony, gaming, or streaming accounts).</p>
                </div>
                
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Ticket Scalping</h3>
                  <p className="text-gray-700">Using proxies to purchase high-demand tickets on platforms that explicitly prohibit proxy use or automated purchases.</p>
                </div>
                
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Ad Fraud or Metrics Manipulation</h3>
                  <p className="text-gray-700">Generating fake ad impressions, fraudulent clicks, or manipulating analytics.</p>
                </div>
                
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Unauthorized Data Collection</h3>
                  <p className="text-gray-700">Scraping websites in violation of their terms of service or collecting sensitive user data without consent.</p>
                </div>
                
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Automated Account Creation</h3>
                  <p className="text-gray-700">Using bots or scripts behind proxies to mass-create user accounts on any platform.</p>
                </div>
                
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Engaging with Dating Sites</h3>
                  <p className="text-gray-700">Automating actions or scraping content from dating platforms.</p>
                </div>
                
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Accessing Restricted or Geo-Blocked Content</h3>
                  <p className="text-gray-700">Circumventing geo-restrictions on platforms that prohibit such access by policy or law.</p>
                </div>
              </div>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Zero Tolerance Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <span className="text-2xl mr-3">🔐</span>
                Illegal or Malicious Use – Zero Tolerance
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You may not use NEXX.NET proxies for any activity that is illegal under your jurisdiction 
                or the jurisdiction of the services you're accessing. This includes, but is not limited to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Phishing, scamming, or identity theft</li>
                <li>Distribution of malware or viruses</li>
                <li>Network scanning, probing, or DDoS</li>
                <li>Copyright infringement or piracy</li>
                <li>Impersonation or credential stuffing</li>
                <li>Fraud or theft in any form</li>
              </ul>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Commitment Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <span className="text-2xl mr-3">🤝</span>
                Our Commitment
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>All proxy IPs are sourced via direct ISP contracts, not resold or recycled.</li>
                <li>We monitor IP quality and reputation continuously.</li>
                <li>We proactively remove and rotate flagged endpoints.</li>
                <li>We reserve the right to suspend or terminate service at any time for violations of this policy.</li>
              </ul>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Service Integrity Clause Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <span className="text-2xl mr-3">📄</span>
                Service Integrity Clause
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To maintain the quality of service for all users and uphold our obligations to providers, 
                we reserve the right to suspend or terminate access—without refund—if we identify behavior 
                that violates these terms, either through an abuse report or our internal investigation.
              </p>
              <p className="text-gray-700 leading-relaxed font-medium">
                We always aim to be fair, but protecting the network comes first.
              </p>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Contact Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                <span className="text-2xl mr-3">✅</span>
                If You're Unsure—Talk to Us
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We're here to build trust. If you're not sure whether your use case fits, ask us before 
                you deploy. We'd rather give you clarity than take action after the fact.
              </p>
              <div className="mt-6">
                <Button className="bg-nexx-green hover:bg-green-700 text-white">
                  Contact Support
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProxyTOS;
